import { cn } from '@forest/shared'
import { createMemo, For, Show, type Accessor } from 'solid-js'
import leyaRocks from '~/assets/leya-rocks.png'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { nextUtcMidnightMs, resolveCountdownTier } from '~/lib/countdown'
import { dailyPicksRefreshEnabled } from '~/lib/feature-flags'
import { m } from '~/paraglide/messages'
import type { CatalogueContent, CatalogueProduct } from '~/services/catalogue'
import {
  hasVisibleSlots,
  slotOwned,
  type CatalogueGridSlot,
} from '~/store/catalogue-grid'
import type { DailyPicksRefreshState } from '~/store/daily-picks-refresh'
import type { DailyPicksRefreshTap } from '~/store/daily-picks-refresh-flow'
import type { HeroSlot } from '~/store/hero-row'
import type { ShopBalances } from '~/store/session-ledger'
import type { StarterPack } from '~/services/starter-pack'
import type { StarterPackSlot } from '~/store/starter-pack'
import {
  CATALOGUE_GRID_CLASS,
  CatalogueGridPlaceholder,
  CatalogueSlot,
} from './catalogue-card'
import { openDailyPicksInfo } from './daily-picks-info'
import { DailyPicksRefreshButton } from './daily-picks-refresh-button'
import { HeroRow, HeroRowPlaceholder } from './hero-row'
import { QuestionCircleIcon } from './icons/question-circle'
import { PlaceholderTextLine } from './placeholder-bar'

// The Daily Picks section's layout and its heading's two lines of type,
// shared by the real section and the boot skeleton's (GLOSSARY.md "Boot
// skeleton"): the bars sit in lines of the same type, so they take the
// lines' heights.
const DAILY_PICKS_SECTION_CLASS = 'pb-[72px]'
const DAILY_PICKS_HEADING_ROW_CLASS =
  'flex items-start justify-between gap-3 px-4 pb-3'
const DAILY_PICKS_TITLE_TYPE_CLASS = 'font-bold'
const DAILY_PICKS_COUNTDOWN_TYPE_CLASS = 'text-sm font-semibold'
// Enough placeholder cards to run past the bottom of any phone screen.
const DAILY_PICKS_PLACEHOLDER_COUNT = 9

// The Featured tab (GLOSSARY.md): the main-product hero row above the Daily
// Picks section — the heading, which opens the Daily Picks explainer, the
// refresh countdown to the next UTC midnight on the Shop server clock, and
// the rotating grid of Classic-style cards in the collection's display order
// — with the Daily Picks refresh button beside the heading. In the exhausted
// pool the refresh button dims; once no unowned goods remain the Super
// Collector card stands in for the countdown, the grid, and the button too,
// and only the heading stays, its explainer with it.
export function FeaturedCatalogue(props: {
  // The refresh button judges its tier's affordability against the session
  // ledger (GLOSSARY.md "Insufficient purchase balance").
  balances: Accessor<ShopBalances | undefined>
  // The entry count minus successful Daily Picks purchases this session.
  dailyPickUnownedCount: number | undefined
  // Distinguishes an empty draw from an assortment whose cards were omitted.
  dailyPickResponseSize: number
  dailyPickSlots: CatalogueGridSlot[]
  heroSlots: HeroSlot[]
  isCelebrating?: (content: CatalogueContent, gid: number) => boolean
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  // The refresh button's tap, carrying the tier it quoted and whether the
  // pool on screen is exhausted (GLOSSARY.md "Exhausted pool").
  onRefresh: DailyPicksRefreshTap
  // The starter-pack card's price pill: buys the pack, no confirmation.
  onPurchaseStarterPack: (pack: StarterPack) => void
  onSelect: (product: CatalogueProduct) => void
  onSelectDailyPick: (product: CatalogueProduct) => void
  // The starter-pack card body's tap: opens the starter-pack detail.
  onSelectStarterPack: (pack: StarterPack) => void
  // The refresh button's tap while its tier is out of reach: the
  // currency-aware top-up navigation, no confirmation.
  onTopUp: (currency: CatalogueCurrency) => void
  // The refresh button's state (GLOSSARY.md "Daily Picks refresh").
  refresh: Accessor<DailyPicksRefreshState>
  // Whether the face-down picks are breathing (GLOSSARY.md "Refresh
  // reveal"): from the grid's turn-over until the new assortment is dealt.
  refreshBreathing: Accessor<boolean>
  // The face every pick shows (GLOSSARY.md "Refresh reveal"): face-down from
  // a committed refresh until its new assortment is on screen.
  refreshFaceDown: Accessor<boolean>
  // True inside the last minute before the rollover (GLOSSARY.md
  // "Last-minute refresh lock"): the button dims but stays tappable.
  refreshLocked: () => boolean
  // True while a Daily Picks refresh is in flight: the button spins and the
  // grid stops taking taps — at full opacity, since its face-down cards'
  // breath is the loading signal (GLOSSARY.md "Refresh reveal") — while the
  // heading, countdown, hero row, tabs, and Close button stay live (ADR 0032).
  refreshPending: () => boolean
  serverNow: Accessor<number>
  // The starter pack's hero slot (GLOSSARY.md "Starter pack"), leading the
  // hero row until owned and trailing it after; undefined = no starter pack.
  starterPack?: StarterPackSlot
  // True while the starter pack shimmer must wait (GLOSSARY.md "Starter pack
  // shimmer (掃光)"): its offer dialog still to come or open.
  starterPackShimmerHeld: () => boolean
  // True while a pick's unlock is still on screen — its success reveal up or
  // its celebration cycle running. Required, not optional: a missing hold
  // silently reintroduces the flip-behind-the-reveal.
  unlockResolving: () => boolean
}) {
  // A daily-pick response with no products is the Super Collector state,
  // so the section stays for its card; a response whose every product web
  // omitted hides the whole section — heading, countdown, and grid
  // (GLOSSARY.md "Empty Shop collection"). Pending slots keep it visible as
  // skeletons while hydration runs.
  const emptyResponse = () => props.dailyPickResponseSize === 0
  const sectionVisible = createMemo(
    () => emptyResponse() || hasVisibleSlots(props.dailyPickSlots),
  )
  const exhaustedPool = createMemo(() => {
    const remaining = props.dailyPickUnownedCount
    return (
      remaining !== undefined &&
      remaining <=
        props.dailyPickSlots.filter((slot) => !slotOwned(slot, props.isOwned))
          .length
    )
  })
  // The count establishes that the pool is fully owned; visible-card
  // ownership alone cannot. Keep the last unlock's reveal, apply prompt,
  // and celebration on screen before replacing its grid (ADR 0034).
  const superCollector = createMemo(() => {
    if (emptyResponse()) return true
    return props.dailyPickUnownedCount === 0 && !props.unlockResolving()
  })
  // The assortment rotates at UTC midnight, so the refresh countdown always
  // sits within the next 24 hours: hours and minutes are the only tiers it
  // needs. The one day-tier case is a target exactly 24 hours out — standing
  // on midnight itself — which reads as 24 hours, not "1 day".
  const countdownCopy = createMemo(() => {
    const now = props.serverNow()
    const tier = resolveCountdownTier(nextUtcMidnightMs(now) - now)
    if (tier.unit === 'min') {
      return m.daily_picks_description_min({ number: tier.count })
    }
    return m.daily_picks_description_hour({
      number: tier.unit === 'day' ? tier.count * 24 : tier.count,
    })
  })

  return (
    <>
      <HeroRow
        heroSlots={props.heroSlots}
        isOwned={props.isOwned}
        onPurchaseStarterPack={props.onPurchaseStarterPack}
        onSelect={props.onSelect}
        onSelectStarterPack={props.onSelectStarterPack}
        serverNow={props.serverNow}
        starterPack={props.starterPack}
        starterPackShimmerHeld={props.starterPackShimmerHeld}
      />
      <Show when={sectionVisible()}>
        <section data-testid="daily-picks" class={DAILY_PICKS_SECTION_CLASS}>
          <div class={DAILY_PICKS_HEADING_ROW_CLASS}>
            <div>
              {/* The whole heading is the explainer's tap target, title and
                  glyph alike; the button's name is the visible title, so the
                  heading still reads as "Daily Picks". */}
              <h2 class={cn(DAILY_PICKS_TITLE_TYPE_CLASS, 'text-ink')}>
                <button
                  type="button"
                  data-testid="daily-picks-info"
                  class="flex items-center gap-0.5"
                  onClick={() => void openDailyPicksInfo()}
                >
                  {m.daily_picks()}
                  <QuestionCircleIcon class="text-ink/70" />
                </button>
              </h2>
              <Show when={!superCollector()}>
                <p
                  data-testid="daily-picks-countdown"
                  class={cn(DAILY_PICKS_COUNTDOWN_TYPE_CLASS, 'text-ink/70')}
                >
                  {countdownCopy()}
                </p>
              </Show>
            </div>
            {/* Absent in the Super Collector state (GLOSSARY.md), whose card
                stands in for the grid; dimmed in the exhausted pool, where a
                re-draw could only deal the same goods or fewer and the tap
                says so; and gated by the release flag (lib/feature-flags.ts). */}
            <Show
              when={
                props.dailyPickUnownedCount !== undefined &&
                !superCollector() &&
                dailyPicksRefreshEnabled
              }
            >
              <DailyPicksRefreshButton
                balances={props.balances}
                exhausted={exhaustedPool()}
                locked={props.refreshLocked()}
                onRefresh={props.onRefresh}
                onTopUp={props.onTopUp}
                pending={props.refreshPending()}
                state={props.refresh()}
              />
            </Show>
          </div>
          <Show
            when={!superCollector()}
            fallback={
              <div class="px-4">
                <div
                  data-testid="daily-picks-done"
                  class="gradient-outline flex items-center gap-2 rounded-[26px] bg-white/35 py-[14px] pl-3 pr-[18px] [--ow:2px]"
                >
                  <img alt="" src={leyaRocks} class="h-24 w-[117px] shrink-0" />
                  <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <p class="text-base font-bold text-ink">
                      {m.daily_picks_done_title()}
                    </p>
                    <p class="text-sm text-ink/70">
                      {m.daily_picks_done_description()}
                    </p>
                  </div>
                </div>
              </div>
            }
          >
            <ul
              data-testid="daily-picks-grid"
              aria-busy={props.refreshPending()}
              class={CATALOGUE_GRID_CLASS}
              classList={{ 'pointer-events-none': props.refreshPending() }}
            >
              <For each={props.dailyPickSlots}>
                {(slot, index) => (
                  <CatalogueSlot
                    disabled={props.refreshPending()}
                    isCelebrating={props.isCelebrating}
                    isOwned={props.isOwned}
                    onSelect={props.onSelectDailyPick}
                    // Display position, so the reveal deals the cards in
                    // the order they are read.
                    reveal={{
                      breathing: props.refreshBreathing,
                      displayIndex: index(),
                      faceDown: props.refreshFaceDown,
                    }}
                    serverNow={props.serverNow}
                    slot={slot}
                  />
                )}
              </For>
            </ul>
          </Show>
        </section>
      </Show>
    </>
  )
}

/**
 * The Featured tab as the boot skeleton draws it: the hero row's first card
 * and the next card's edge, then the Daily Picks heading's title and
 * countdown over its card grid, every block on the real one's footprint.
 * The design draws no refresh button; the heading text sets the row's
 * height either way.
 */
export function FeaturedCataloguePlaceholder() {
  const line = (typeClass: string, width: string) => (
    <PlaceholderTextLine class={cn('h-[13px]', width)} typeClass={typeClass} />
  )
  return (
    <>
      <HeroRowPlaceholder />
      <section class={DAILY_PICKS_SECTION_CLASS}>
        <div class={DAILY_PICKS_HEADING_ROW_CLASS}>
          <div>
            {line(DAILY_PICKS_TITLE_TYPE_CLASS, 'w-[83px]')}
            {line(DAILY_PICKS_COUNTDOWN_TYPE_CLASS, 'w-[238px]')}
          </div>
        </div>
        <CatalogueGridPlaceholder count={DAILY_PICKS_PLACEHOLDER_COUNT} />
      </section>
    </>
  )
}
